// frontend/src/components/ConnectionTest.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { api } from '../services/api';
import { debugConnection } from '../utils/debugConnection';
import { useLanguage } from '../contexts/LanguageContext';

const ConnectionTest = ({ show = false, onClose }) => {
  const [testResults, setTestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('unknown');

  const { t } = useLanguage();

  useEffect(() => {
    if (show) {
      runConnectionTest();
    }
  }, [show]);

  const runConnectionTest = async () => {
    setIsRunning(true);
    setConnectionStatus('testing');
    
    try {
      const results = await debugConnection();
      setTestResults(results);
      
      // Determine overall status
      const failures = results.tests.filter(test => test.status === 'failed');
      if (failures.length === 0) {
        setConnectionStatus('good');
      } else if (failures.length < results.tests.length / 2) {
        setConnectionStatus('partial');
      } else {
        setConnectionStatus('poor');
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionStatus('failed');
    } finally {
      setIsRunning(false);
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'good':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'partial':
        return <AlertTriangle className="text-yellow-500" size={24} />;
      case 'poor':
      case 'failed':
        return <WifiOff className="text-red-500" size={24} />;
      case 'testing':
        return <RefreshCw className="text-blue-500 animate-spin" size={24} />;
      default:
        return <Wifi className="text-gray-500" size={24} />;
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'good':
        return t('connectionIsWorkingWell');
      case 'partial':
        return t('someConnectionIssuesDetected');
      case 'poor':
        return t('multipleConnectionProblems');
      case 'failed':
        return t('connectionTestFailed');
      case 'testing':
        return t('testingConnection');
      default:
        return t('connectionStatusUnknown');
    }
  };

  const getSolutions = () => {
    if (!testResults) return [];
    
    const solutions = [];
    const failures = testResults.tests.filter(test => test.status === 'failed');
    
    failures.forEach(failure => {
      if (failure.error.includes('ERR_BLOCKED_BY_CLIENT')) {
        solutions.push({
          title: t('adBlockerDetected'),
          description: t('disableAdBlockerForLocalhost'),
          action: t('addLocalhostToWhitelist')
        });
      } else if (failure.error.includes('Failed to fetch')) {
        solutions.push({
          title: t('serverNotResponding'),
          description: t('backendServerMayNotBeRunning'),
          action: t('startBackendOnPort5001')
        });
      } else if (failure.error.includes('CORS')) {
        solutions.push({
          title: t('corsIssues'),
          description: t('crossOriginRequestBlocked'),
          action: t('checkCorsConfiguration')
        });
      }
    });
    
    return solutions;
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {getStatusIcon()}
              <div>
                <h2 className="text-xl font-bold text-white mb-2">{t('connectionDiagnostics')}</h2>
                <p className="text-gray-400 text-sm">{getStatusText()}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              ×
            </button>
          </div>

          {/* Test Results */}
          {testResults && (
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-white">{t('testResults')}</h3>
              {testResults.tests.map((test, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {test.status === 'success' || test.status === 'accessible' ? (
                      <CheckCircle className="text-green-500" size={20} />
                    ) : (
                      <AlertTriangle className="text-red-500" size={20} />
                    )}
                    <div>
                      <p className="text-white font-medium">{test.test}</p>
                      {test.error && (
                        <p className="text-red-400 text-sm">{test.error}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    test.status === 'success' || test.status === 'accessible'
                      ? 'bg-green-900 text-green-300'
                      : 'bg-red-900 text-red-300'
                  }`}>
                    {test.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Solutions */}
          {getSolutions().length > 0 && (
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-white">{t('recommendedSolutions')}</h3>
              {getSolutions().map((solution, index) => (
                <div key={index} className="p-4 bg-yellow-900/20 border border-yellow-600 rounded-lg">
                  <h4 className="text-yellow-400 font-medium">{solution.title}</h4>
                  <p className="text-gray-300 text-sm mt-1">{solution.description}</p>
                  <p className="text-yellow-300 text-sm mt-2 font-medium">
                    {t('action')}: {solution.action}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Quick Fixes */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold text-white">{t('quickFixes')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => window.location.reload()}
                className="p-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
              >
                {t('refreshPage')}
              </button>
              <button
                onClick={runConnectionTest}
                disabled={isRunning}
                className="p-3 bg-gray-600 hover:bg-gray-700 rounded-lg text-white text-sm transition-colors disabled:opacity-50"
              >
                {isRunning ? t('testingConnection') : t('retestConnection')}
              </button>
              <button
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
                className="p-3 bg-orange-600 hover:bg-orange-700 rounded-lg text-white text-sm transition-colors"
              >
                {t('clearCacheAndReload')}
              </button>
              <button
                onClick={() => window.open('http://localhost:5001/health', '_blank')}
                className="p-3 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors"
              >
                {t('testBackendDirectly')}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              {t('close')}
            </button>
            <button
              onClick={() => {
                console.log('Full diagnostic data:', testResults);
                alert(t('diagnosticDataLogged'));
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              {t('exportLogs')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionTest;
